import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Paper elevation={1} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to SurveySlack. We respect your privacy and are committed to protecting 
              your personal data. This privacy policy explains how we collect, use, and protect 
              your information when you use our survey platform.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              2. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information you provide directly to us, such as:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Account information (email, name)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Survey data and responses" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Usage data and analytics" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Communication preferences" />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              3. Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar technologies to:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Necessary Cookies" 
                  secondary="Essential for website functionality and security"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Analytics Cookies" 
                  secondary="Help us understand how you use our website to improve your experience"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Functional Cookies" 
                  secondary="Enable enhanced features and personalization"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Advertising Cookies" 
                  secondary="Used to deliver relevant advertisements (if applicable)"
                />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              4. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use your information to:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Provide and maintain our services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Process and analyze survey data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Communicate with you about our services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Improve our platform and user experience" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Ensure security and prevent fraud" />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              5. Your Rights and Choices
            </Typography>
            <Typography variant="body1" paragraph>
              You have the right to:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Access your personal data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Correct inaccurate data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Delete your data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Object to processing" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Data portability" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Withdraw consent at any time" />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              6. Cookie Management
            </Typography>
            <Typography variant="body1" paragraph>
              You can manage your cookie preferences at any time by:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Using our cookie consent banner when it appears" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Adjusting your browser settings to block or delete cookies" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Contacting us to update your preferences" />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              7. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect 
              your personal data against unauthorized access, alteration, disclosure, or 
              destruction. However, no method of transmission over the internet is 100% secure.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              8. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal data only for as long as necessary to fulfill the 
              purposes outlined in this privacy policy, unless a longer retention period 
              is required or permitted by law.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              9. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this privacy policy or our data practices, 
              please contact us at:
            </Typography>
            <Typography variant="body1">
              Email: privacy@surveyslack.com<br />
              Address: [Your Company Address]
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              10. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this privacy policy from time to time. We will notify you of 
              any changes by posting the new privacy policy on this page and updating the 
              "Last updated" date.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;